import { useEffect, useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  Box,
  HStack,
  VStack,
  Spinner,
  Badge,
  Divider,
} from '@chakra-ui/react'
import { CheckCircle2, Mail, PhoneCall, Ticket, Trash2, X } from 'lucide-react'
import { getTicketRequestSourceEmail } from '@/lib/services/ticketRequestService'

function formatSubmittedAt(value: string | null | undefined): string {
  if (!value) return '–'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  const day = d.getDate()
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = months[d.getMonth()]
  const year = d.getFullYear()
  const hours = d.getHours()
  const minutes = d.getMinutes()
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const h12 = hours % 12 || 12
  const minStr = minutes < 10 ? `0${minutes}` : String(minutes)
  return `${day} ${month} ${year} - ${h12}:${minStr} ${ampm}`
}

function formatSource(value: string | null | undefined): string {
  if (!value) return '–'
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

type Props = {
  isOpen: boolean
  onClose: () => void
  row: Record<string, unknown> | null
  /** When true, show a Delete control (soft delete is implemented by the parent). */
  showDelete?: boolean
  onDelete?: () => void
  isDeleting?: boolean
  showAcknowledge?: boolean
  onAcknowledge?: () => void
  isAcknowledging?: boolean
  showCreateTicket?: boolean
  onCreateTicket?: () => void
  onComposeEmail?: (row: Record<string, unknown>) => void
  onCallOut?: (row: Record<string, unknown>) => void
  /** When false, hides Compose Email and Call out (e.g. Trash tab). */
  showCommunication?: boolean
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Text fontSize="xs" color="gray.500" fontWeight="600" mb={1}>
        {label}
      </Text>
      <Box fontSize="sm" lineHeight="short" whiteSpace="pre-wrap">
        {value ?? '–'}
      </Box>
    </Box>
  )
}

function getReceivedEmailId(row: Record<string, unknown> | null): number | null {
  if (!row?.raw_payload || typeof row.raw_payload !== 'object') return null
  const id = (row.raw_payload as Record<string, unknown>).received_email_id
  if (typeof id === 'number' && id > 0) return id
  if (typeof id === 'string' && id.trim() !== '') {
    const n = Number(id)
    return Number.isFinite(n) && n > 0 ? n : null
  }
  return null
}

function getTicketRequestId(row: Record<string, unknown>): number | null {
  const id = row.id
  if (typeof id === 'number' && id > 0) return id
  if (typeof id === 'string' && id.trim() !== '') {
    const n = Number(id)
    return Number.isFinite(n) && n > 0 ? n : null
  }
  return null
}

function shouldLoadSourceEmail(row: Record<string, unknown>): boolean {
  if (getReceivedEmailId(row) != null) return true
  const extRef = row.external_reference_id
  if (typeof extRef === 'string' && extRef.trim() !== '') return true
  if (row.request_source === 'email' || row.request_channel === 'email') return true
  return false
}

/** Stored ticket text; skips seeder placeholder when description is available. */
function resolveStoredRequestDetails(row: Record<string, unknown>): string | null {
  const raw = typeof row.raw_message === 'string' ? row.raw_message.trim() : ''
  const desc = typeof row.description === 'string' ? row.description.trim() : ''
  if (raw.startsWith('Sample raw input for ')) {
    return desc || null
  }
  if (raw) return raw
  return desc || null
}

function looksLikeHtml(s: string): boolean {
  return /<[a-z][\s\S]*>/i.test(s)
}

/** Wrapped HTML for iframe srcDoc; kept out of JSX to avoid `}` in CSS breaking the parser. */
function buildEmailIframeSrcDoc(emailHtml: string): string {
  const safeBody = emailHtml.replace(/<\/script/gi, '<\\/script')
  const csp =
    "default-src 'none'; img-src https: data: cid:; style-src 'unsafe-inline'; base-uri 'none'"
  const css =
    'body{font-family:system-ui,-apple-system,sans-serif;font-size:14px;line-height:1.5;margin:12px;color:#111;word-break:break-word;} img{max-width:100%;height:auto;}'
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><style>${css}</style></head><body>${safeBody}</body></html>`
}

export function TicketRequestDetailModal({
  isOpen,
  onClose,
  row,
  showDelete = false,
  onDelete,
  isDeleting = false,
  showAcknowledge = false,
  onAcknowledge,
  isAcknowledging = false,
  showCreateTicket = false,
  onCreateTicket,
  onComposeEmail,
  onCallOut,
  showCommunication = true,
}: Props) {
  const [liveEmail, setLiveEmail] = useState<{ subject: string; body: string } | null>(null)
  const [isFetchingEmail, setIsFetchingEmail] = useState(false)

  useEffect(() => {
    if (!isOpen || !row) {
      setLiveEmail(null)
      setIsFetchingEmail(false)
      return
    }

    const ticketId = getTicketRequestId(row)

    if (!shouldLoadSourceEmail(row) || ticketId == null) {
      setLiveEmail(null)
      setIsFetchingEmail(false)
      return
    }

    let cancelled = false
    setLiveEmail(null)
    setIsFetchingEmail(true)

    getTicketRequestSourceEmail(ticketId)
      .then((data) => {
        if (cancelled) return
        setLiveEmail({
          subject: data.subject ?? '',
          body: data.body ?? '',
        })
      })
      .catch(() => {
        if (cancelled) return
        setLiveEmail(null)
      })
      .finally(() => {
        if (!cancelled) setIsFetchingEmail(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, row])

  if (!row) return null

  const requestNumber = (row.request_number as string) ?? '–'
  const clientName = (row.client_name ?? row.contact_name) as string | undefined
  const requestType = row.service_type
  const requestTypeLabel =
    typeof requestType === 'object' && requestType !== null && 'name' in requestType
      ? (requestType as { name?: string }).name
      : requestType != null
        ? String(requestType)
        : '–'
  const source = formatSource(row.request_source as string | undefined)
  const submittedAt = formatSubmittedAt(row.submitted_at as string | undefined)
  const acknowledgedAt = formatSubmittedAt(row.acknowledged_at as string | undefined)
  const contactEmail = (row.contact_email as string) ?? '–'
  const extractedFields =
    row.extracted_fields != null && typeof row.extracted_fields === 'object'
      ? (row.extracted_fields as Record<string, unknown>)
      : null
  const subjectRaw =
    (liveEmail?.subject && liveEmail.subject.trim() !== '' ? liveEmail.subject : undefined) ??
    (row.email_subject as string | undefined) ??
    (row.subject as string | undefined) ??
    (extractedFields?.email_subject as string | undefined) ??
    (extractedFields?.subject_normalized as string | undefined)
  const subject =
    subjectRaw != null && String(subjectRaw).trim() !== '' ? String(subjectRaw).trim() : '–'

  const storedDetails = resolveStoredRequestDetails(row)
  const emailBody =
    liveEmail?.body && liveEmail.body.trim() !== '' ? liveEmail.body : storedDetails
  const emailBodyIsHtml = emailBody != null && looksLikeHtml(emailBody)
  const wantsSourceEmail = shouldLoadSourceEmail(row)

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        h={{ base: '100dvh', sm: '90dvh' }}
        maxH={{ base: '100dvh', sm: '90dvh' }}
        w={{ base: '100vw', md: '95vw' }}
        maxW="min(1280px, 95vw)"
        display="flex"
        flexDirection="column"
        my={{ base: 0, sm: 'auto' }}
        borderRadius={{ base: 0, sm: 'md' }}
      >
        <ModalHeader flexShrink={0} py={4} px={5} pr={12}>
          <HStack spacing={3} flexWrap="wrap" align="center">
            <Text fontSize="lg" fontWeight="semibold">
              Request Details
            </Text>
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              {requestNumber}
            </Text>
            <Badge
              colorScheme={row.acknowledged_at ? 'green' : 'gray'}
              variant="outline"
              fontSize="xs"
            >
              {row.acknowledged_at ? 'Acknowledged' : 'Pending'}
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={3} right={3} />
        <ModalBody
          flex="1"
          minH={0}
          overflow="hidden"
          display="flex"
          flexDirection="column"
          px={5}
          pb={3}
        >
          <HStack
            align="stretch"
            spacing={0}
            flex="1"
            minH={0}
            flexDir={{ base: 'column', lg: 'row' }}
          >
            {/* Column 1 — request metadata */}
            <VStack
              align="stretch"
              spacing={3}
              flexShrink={0}
              w={{ base: 'full', lg: '280px' }}
              maxW={{ base: 'full', lg: '280px' }}
              pr={{ base: 0, lg: 4 }}
              pb={{ base: 4, lg: 0 }}
              borderRightWidth={{ base: 0, lg: '1px' }}
              borderBottomWidth={{ base: '1px', lg: 0 }}
              borderColor="gray.100"
              _dark={{ borderColor: 'whiteAlpha.200' }}
              overflowY={{ lg: 'auto' }}
            >
              <DetailRow label="Contact Email" value={contactEmail} />
              <DetailRow label="Request Number" value={requestNumber} />
              <DetailRow label="Client" value={clientName ?? '–'} />
              <DetailRow label="Request Type" value={requestTypeLabel} />
              <Divider borderColor="gray.100" _dark={{ borderColor: 'whiteAlpha.200' }} />
              <DetailRow label="Submitted At" value={submittedAt} />
              <DetailRow
                label="Acknowledged At"
                value={row.acknowledged_at ? acknowledgedAt : '–'}
              />
              <Divider borderColor="gray.100" _dark={{ borderColor: 'whiteAlpha.200' }} />
              <DetailRow label="Source" value={source} />
              <DetailRow label="Subject" value={subject} />
            </VStack>

            {/* Column 2 — request details (wider) */}
            <Box
              flex="1"
              minH={0}
              minW={0}
              pl={{ base: 0, lg: 5 }}
              pt={{ base: 4, lg: 0 }}
              display="flex"
              flexDirection="column"
            >
              <Text fontSize="xs" color="gray.500" fontWeight="600" mb={1} flexShrink={0}>
                Request details
              </Text>
              {isFetchingEmail && (
                <HStack spacing={2} flexShrink={0} py={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color="gray.500">
                    Loading email…
                  </Text>
                </HStack>
              )}
              {!isFetchingEmail && emailBody != null && (
                <Box flex="1" minH={0} display="flex" flexDirection="column">
                  {emailBodyIsHtml ? (
                    <Box
                      flex="1"
                      minH={{ base: '200px', lg: 0 }}
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      overflow="hidden"
                      bg="gray.50"
                      _dark={{ bg: 'gray.900', borderColor: 'gray.600' }}
                    >
                      <iframe
                        title="Request details"
                        sandbox=""
                        srcDoc={buildEmailIframeSrcDoc(emailBody)}
                        style={{
                          width: '100%',
                          height: '100%',
                          minHeight: '200px',
                          border: 'none',
                          display: 'block',
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      flex="1"
                      minH={0}
                      overflowY="auto"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={3}
                      bg="gray.50"
                      _dark={{ bg: 'gray.900', borderColor: 'gray.600' }}
                    >
                      <Text fontSize="sm" whiteSpace="pre-wrap">
                        {emailBody}
                      </Text>
                    </Box>
                  )}
                </Box>
              )}
              {!isFetchingEmail && emailBody == null && (
                <Box
                  flex="1"
                  minH={{ base: '120px', lg: 0 }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  borderStyle="dashed"
                  p={4}
                  _dark={{ borderColor: 'gray.600' }}
                >
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    {wantsSourceEmail &&
                    (getReceivedEmailId(row) != null ||
                      (typeof row.external_reference_id === 'string' &&
                        row.external_reference_id.trim() !== ''))
                      ? 'Unable to load email from source.'
                      : wantsSourceEmail
                        ? 'No source email linked to this request.'
                        : 'No request details available.'}
                  </Text>
                </Box>
              )}
            </Box>
          </HStack>
        </ModalBody>
        <ModalFooter
          flexShrink={0}
          borderTopWidth="1px"
          borderColor="gray.100"
          _dark={{ borderColor: 'whiteAlpha.200' }}
          py={3}
          px={5}
        >
          <HStack justify="space-between" w="100%">
            <Box>
              {showDelete && onDelete ? (
                <Button
                  colorScheme="red"
                  variant="outline"
                  size="sm"
                  leftIcon={<Trash2 size={16} />}
                  onClick={onDelete}
                  isLoading={isDeleting}
                  isDisabled={isDeleting}
                >
                  Delete
                </Button>
              ) : (
                <Box />
              )}
            </Box>
            <HStack spacing={2} flexWrap="wrap" justify="flex-end">
              {showAcknowledge && onAcknowledge ? (
                <Button
                  leftIcon={<CheckCircle2 size={16} />}
                  colorScheme="blue"
                  size="sm"
                  onClick={onAcknowledge}
                  isLoading={isAcknowledging}
                  isDisabled={isAcknowledging}
                >
                  Acknowledge
                </Button>
              ) : null}
              {showCreateTicket && onCreateTicket ? (
                <Button
                  leftIcon={<Ticket size={16} />}
                  colorScheme="green"
                  size="sm"
                  onClick={onCreateTicket}
                >
                  Create Ticket
                </Button>
              ) : null}
              {showCommunication ? (
                <>
                  <Button
                    leftIcon={<Mail size={16} />}
                    variant="outline"
                    size="sm"
                    onClick={() => onComposeEmail?.(row)}
                    isDisabled={!row?.contact_email}
                  >
                    Compose Email
                  </Button>
                  <Button
                    leftIcon={<PhoneCall size={16} />}
                    colorScheme="green"
                    variant="outline"
                    size="sm"
                    onClick={() => onCallOut?.(row)}
                  >
                    Call out
                  </Button>
                </>
              ) : null}
              <Button size="sm" variant="ghost" onClick={onClose} leftIcon={<X size={16} />}>
                Close
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
